/* =============================================================================
   The document's docked audio bar.

   It owns NO media engine. The element, the transport, the seek bar, the speed
   menu and the download are all `MediaPlayer` — the same component the class
   videos use — and this file is the dock around it: which recording, what it is
   called, previous, next, close.

   It used to hold a `new Audio()` with its own time formatting and its own seek
   input, which was a second media implementation with a second set of bugs.
   ============================================================================= */
import { MediaPlayer } from "../media/MediaPlayer";
import { useMediaQueue, type MediaQueue } from "../media/useMediaQueue";
import type { Material } from "./materials";
import { Icon } from "../ui/Icon";

export type AudioQueue = MediaQueue<Material>;

/** The document's recordings, in editor order, as a queue. */
export function useAudioQueue(materials: Material[]): AudioQueue {
  return useMediaQueue(materials.filter((m) => m.audioUrl));
}

export function DocumentPlayer({
  queue,
  documentTitle,
}: {
  queue: AudioQueue;
  documentTitle: string;
}) {
  const m = queue.current;
  if (!m || !m.audioUrl) return null;

  return (
    <div className="doc-player" role="region" aria-label="Now playing">
      {/* A recording has no artwork anywhere on this page, so the dock wears
          the same headphones mark the tile does rather than an empty plate. */}
      <span className="media-frame doc-player__art">
        <Icon name="headphones" size="1.15em" />
      </span>

      <span className="doc-player__who">
        <span className="doc-player__title">{m.title}</span>
        <span className="doc-player__sub">{documentTitle}</span>
      </span>

      <span className="doc-player__controls">
        <button
          type="button"
          className="btn-app-ghost is-small"
          onClick={() => queue.step(-1)}
          aria-label="Previous recording"
        >
          <Icon name="skip-prev" size="1em" />
        </button>
        <button
          type="button"
          className="btn-app-ghost is-small"
          onClick={() => queue.step(1)}
          aria-label="Next recording"
        >
          <Icon name="skip-next" size="1em" />
        </button>
      </span>

      {/* The player itself: play/pause, the seek bar, the clock, volume, speed
          and — when the file is ours to hand out — download. */}
      <span className="doc-player__engine">
        <MediaPlayer
          source={{ kind: "file", url: m.audioUrl, mime: null, media: "audio" }}
          title={m.title}
          downloadUrl={m.downloadUrl}
          autoPlay
          onEnded={queue.onEnded}
          onReady={queue.attach}
        />
      </span>

      <button
        type="button"
        className="btn-app-ghost is-small doc-player__close"
        onClick={queue.stop}
        aria-label="Close player"
      >
        <Icon name="xmark" size="1em" />
      </button>
    </div>
  );
}
